import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stack,
} from '@mui/material';
import MainLayout from './layout/MainLayout';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CoffeeIcon from '@mui/icons-material/Coffee';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WcIcon from '@mui/icons-material/Wc';
import HelpIcon from '@mui/icons-material/Help';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { brand } from '../theme';
import { BOOTH_SIZE_DESCRIPTION, BOOTH_SIZE_SHORT } from '../utils/boothInfo';
import { FESTIVAL_HOURS_LABEL } from '../utils/festivalDates';

const sectionListItem = {
  primaryTypographyProps: { fontWeight: 600, fontSize: '1.125rem', color: brand.navy },
  secondaryTypographyProps: { fontSize: '1.0625rem', color: brand.textPrimary },
};

const ExhibitorInfo = () => {
  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5 } }}>
          <Typography variant="h1" sx={{ color: brand.navy, mb: 1.5 }}>
            For Exhibitors
          </Typography>
          <Typography
            sx={{
              color: brand.textSecondary,
              fontSize: '1.1875rem',
              maxWidth: 560,
              mx: 'auto',
              mb: 3,
            }}
          >
            Everything our vendors need for a smooth festival day — pricing,
            policies, parking, and contacts.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              href="/Gingham Tree Exhibitors Bulletin 2026.pdf"
              download
              size="large"
              sx={{ minWidth: 240 }}
            >
              Download Bulletin
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              href="/Kids Zone Application 2026.pdf"
              download
              size="large"
              sx={{ minWidth: 240 }}
            >
              Kids Zone Application
            </Button>
          </Stack>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.cream,
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 1 }}>
            Welcome to Gingham Tree 2026!
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: brand.textPrimary }}>
            The Lucedale Fine Arts Club sincerely hopes the day is both pleasant
            and profitable for you. To help the day go smoothly, we have put
            together the following information for your benefit.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.paper,
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 1 }}>
            Booth size
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: brand.textPrimary, mb: 3 }}>
            {BOOTH_SIZE_DESCRIPTION} Plan your tables, canopy, and display materials
            for a {BOOTH_SIZE_SHORT} space.
          </Typography>
          <Typography variant="h3" sx={{ color: brand.navy, mb: 2 }}>
            2026 Booth Pricing
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <PricingCard
              title="Regular Booths"
              rows={[
                ['Without Electricity', '$70'],
                ['With Electricity', '$75'],
              ]}
            />
            <PricingCard
              title="Corner Booths"
              rows={[
                ['Without Electricity', '$80'],
                ['With Electricity', '$85'],
              ]}
            />
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.paper,
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 2 }}>
            Day-of details
          </Typography>
          <List sx={{ p: 0 }}>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <EventIcon />
              </ListItemIcon>
              <ListItemText
                primary="Festival Hours"
                secondary={`The market is open to shoppers ${FESTIVAL_HOURS_LABEL} on festival day.`}
                {...sectionListItem}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <CoffeeIcon />
              </ListItemIcon>
              <ListItemText
                primary="Morning Refreshments"
                secondary="FREE coffee and doughnuts available from 6:00 to 9:00 a.m. at the Jaycees Building. Each booth is entitled to two cups of coffee and two doughnuts. Please wear your name tag for identification."
                {...sectionListItem}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <DirectionsCarIcon />
              </ListItemIcon>
              <ListItemText
                primary="Parking Information"
                secondary="After unloading, exhibitors will be directed to a specified parking area. This allows more parking for shoppers. We have organized the parking situation to be the most efficient for the flow of traffic."
                {...sectionListItem}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <WcIcon />
              </ListItemIcon>
              <ListItemText
                primary="Restroom Locations"
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: '1rem', color: brand.textSecondary }}>
                      Restroom facilities will be located at:
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, pl: 2.5, color: brand.textPrimary }}>
                      <li>Outside of the Jaycees Building</li>
                      <li>Inside of the Jaycees Building</li>
                      <li>Arena's smaller concession stand</li>
                      <li>Arena's larger concession stand</li>
                    </Box>
                  </Box>
                }
                primaryTypographyProps={sectionListItem.primaryTypographyProps}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText
                primary="Need Help?"
                secondary="Look for the Gingham Aprons worn by the Fine Arts Club members if you need assistance. On Friday, volunteers help vendors unload beginning at 3:00 PM, and on Saturday from 6 - 8 AM. PLEASE REMEMBER TO TIP YOUR VOLUNTEERS!"
                {...sectionListItem}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <AttachMoneyIcon />
              </ListItemIcon>
              <ListItemText
                primary="Sales Tax Information"
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: '1rem', color: brand.textSecondary }}>
                      Every exhibitor is required to collect and remit sales tax on all sales made during the festival.
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, pl: 2.5, color: brand.textPrimary }}>
                      <li>Tax envelopes will be collected starting at 3:00 p.m.</li>
                      <li>ALL TAX ENVELOPES MUST BE TURNED IN BY 4:00 P.M.</li>
                      <li>Make checks payable to Lucedale Fine Arts Club</li>
                      <li>The Mississippi State Tax Commission has requested a list of names and addresses of all exhibitors</li>
                    </Box>
                  </Box>
                }
                primaryTypographyProps={sectionListItem.primaryTypographyProps}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <RestaurantIcon />
              </ListItemIcon>
              <ListItemText
                primary="Concessions"
                secondary="A variety of food items and drinks can be purchased from the many food vendors at the festival."
                {...sectionListItem}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <SmokingRoomsIcon />
              </ListItemIcon>
              <ListItemText
                primary="No Smoking Policy"
                secondary="NO SMOKING OR VAPING ON THE GROUNDS."
                {...sectionListItem}
              />
            </ListItem>
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: '#FFF6E6',
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 1 }}>
            Refund Policy
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#B23A2A',
              fontSize: '1.0625rem',
              mb: 1,
              letterSpacing: '0.02em',
            }}
          >
            NO REFUNDS - ALL PAYMENTS ARE FINAL
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: brand.textPrimary }}>
            All booth payments are final and non-refundable under any
            circumstances, including weather, personal emergencies, vendor
            cancellation or no-show, and event cancellation. Please ensure you
            are fully committed before completing booth registration and
            payment.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.paper,
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 2 }}>
            Important policies
          </Typography>
          <List sx={{ p: 0 }}>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <WarningIcon />
              </ListItemIcon>
              <ListItemText
                primary="Booth Viewing"
                secondary="Club members will be viewing each booth. We reserve the right to exclude the exhibition of any works deemed unsuitable (not handmade, etc.) for display. If your items are not handcrafted by you or embellished by you in some way — such as sublimation, vinyl, embroidery, or paint — they will not be allowed."
                {...sectionListItem}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <WarningIcon />
              </ListItemIcon>
              <ListItemText
                primary="Not Allowed"
                secondary={
                  <Box component="ul" sx={{ mt: 1, pl: 2.5, color: brand.textPrimary }}>
                    <li>NO PRE-PURCHASED ITEMS TO RESALE (for example, boutique clothing or jewelry)</li>
                    <li>NO ASSEMBLY KITS, FACTORY-MADE ITEMS, OR IMPORTS</li>
                    <li>NO ITEMS PURCHASED FROM AMAZON OR ANY OTHER COMPANY TO RESALE</li>
                    <li>NO FOOD ITEMS, INCLUDING CAKES, COOKIES, CANDY, PECANS, ETC.</li>
                    <li>NO RAFFLE TICKETS</li>
                  </Box>
                }
                primaryTypographyProps={sectionListItem.primaryTypographyProps}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText
                primary="Allowed Items"
                secondary={
                  <Box component="ul" sx={{ mt: 1, pl: 2.5, color: brand.textPrimary }}>
                    <li>Homemade jar goods such as jellies, jams, pickles, and honey</li>
                    <li>Items you embellish yourself through sublimation, vinyl, embroidery, paint, etc.</li>
                  </Box>
                }
                primaryTypographyProps={sectionListItem.primaryTypographyProps}
              />
            </ListItem>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                <EventIcon />
              </ListItemIcon>
              <ListItemText
                primary="Registration for Next Year"
                secondary={
                  <Box component="ul" sx={{ mt: 1, pl: 2.5, color: brand.textPrimary }}>
                    <li>Pre-registration begins at 2:00 PM on the day of the festival</li>
                    <li>Only vendors who prepay will be guaranteed their same booth number for the next year</li>
                    <li>Mailed-in applications are no longer accepted</li>
                    <li>If you do not pre-register, you must register online at www.ginghamtree.com after June 1</li>
                    <li>Booth space is allocated on a "FIRST COME, FIRST SERVE" basis</li>
                  </Box>
                }
                primaryTypographyProps={sectionListItem.primaryTypographyProps}
              />
            </ListItem>
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.cream,
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 1 }}>
            Always the second Saturday in November
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: brand.textPrimary, mb: 1 }}>
            Festival hours: {FESTIVAL_HOURS_LABEL}
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: brand.textPrimary }}>
            We hope to see all of you next year!
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.paper,
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 2 }}>
            Contact Information
          </Typography>
          <List dense sx={{ p: 0 }}>
            <ContactRow
              icon={<LocationOnIcon />}
              primary="Mailing Address"
              secondary="Gingham Tree, P.O. Box 534, Lucedale, MS 39452"
            />
            <ContactRow primary="Chairperson" secondary="Gina Harris - 601-508-0170" />
            <ContactRow primary="Email" secondary="contact@ginghamtree.com" />
            <ContactRow primary="Website" secondary="www.ginghamtree.com" />
            <ContactRow primary="Food Chairman" secondary="Audra Rouse - 601-947-0761" />
            <ContactRow primary="Kids Zone Area" secondary="601-508-0170 or contact@ginghamtree.com" />
          </List>
        </Paper>
      </Container>
    </MainLayout>
  );
};

const PricingCard = ({ title, rows }) => (
  <Box
    sx={{
      p: 2.5,
      borderRadius: 2,
      border: `1px solid ${brand.hairline}`,
      backgroundColor: brand.cream,
    }}
  >
    <Typography
      sx={{
        fontFamily: '"Fraunces", Georgia, serif',
        fontWeight: 600,
        color: brand.navy,
        fontSize: '1.25rem',
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    {rows.map(([label, price]) => (
      <Box
        key={label}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          py: 0.5,
          fontSize: '1.0625rem',
        }}
      >
        <Box sx={{ color: brand.textPrimary }}>{label}</Box>
        <Box sx={{ fontWeight: 600, color: brand.navy }}>{price}</Box>
      </Box>
    ))}
  </Box>
);

const ContactRow = ({ icon, primary, secondary }) => (
  <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
    {icon && <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>{icon}</ListItemIcon>}
    <ListItemText
      primary={primary}
      secondary={secondary}
      primaryTypographyProps={{ fontWeight: 600, fontSize: '1.0625rem', color: brand.navy }}
      secondaryTypographyProps={{ fontSize: '1.0625rem', color: brand.textPrimary }}
      sx={!icon ? { pl: '56px' } : undefined}
    />
  </ListItem>
);

export default ExhibitorInfo;
